import { Component } from '@theme/component';

/**
 * Solution concentration calculator: mass ÷ volume arithmetic for prepared
 * research solutions. Deliberately has no syringe/units concept and no
 * "dose" framing — this is general lab dilution math, not an
 * administration or consumption aid.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} vialAmount
 * @property {HTMLInputElement} bacWater
 * @property {HTMLInputElement} desiredDose
 * @property {HTMLElement} concentrationOutput
 * @property {HTMLElement} volumeOutput
 * @property {HTMLElement} dosesPerVialOutput
 *
 * @extends Component<Refs>
 */
export class DosageCalculator extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.calculate();
  }

  calculate = () => {
    const { refs } = this;

    const vialMg = Number(refs.vialAmount?.value) || 0;
    const solventMl = Number(refs.bacWater?.value) || 0;
    const aliquotMg = Number(refs.desiredDose?.value) || 0;

    if (vialMg <= 0 || solventMl <= 0) {
      this.#setText(refs.concentrationOutput, '—');
      this.#setText(refs.volumeOutput, '—');
      this.#setText(refs.dosesPerVialOutput, '—');
      return;
    }

    const concentrationMgPerMl = vialMg / solventMl;
    this.#setText(refs.concentrationOutput, `${this.#round(concentrationMgPerMl)} mg/mL`);

    if (aliquotMg <= 0) {
      this.#setText(refs.volumeOutput, '—');
      this.#setText(refs.dosesPerVialOutput, '—');
      return;
    }

    const volumeMl = aliquotMg / concentrationMgPerMl;
    const aliquotsPerVial = vialMg / aliquotMg;

    this.#setText(refs.volumeOutput, `${volumeMl.toFixed(3)} mL`);
    this.#setText(refs.dosesPerVialOutput, `${Math.floor(aliquotsPerVial)} aliquots`);
  };

  /**
   * @param {number} value
   */
  #round(value) {
    return Math.round(value * 100) / 100;
  }

  /**
   * @param {HTMLElement | undefined} element
   * @param {string} text
   */
  #setText(element, text) {
    if (element) element.textContent = text;
  }
}

if (!customElements.get('dosage-calculator')) {
  customElements.define('dosage-calculator', DosageCalculator);
}
